import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  createPermit,
  updatePermit,
  getPermit,
  // uploadPermitFile,
  // deletePermitAllFiles,
} from "../../api/permit";
import { v4 as uuid } from "uuid";
import MobileAttachmentBox from "../../components/common/papers/MobileAttachmentBox";

export default function PermitFormPage() {
  const navigate = useNavigate();
  const { id, permitId } = useParams();
  const isEdit = Boolean(permitId);

  const [name, setName] = useState("");
  const [permitType, setPermitType] = useState("");
  const [authority, setAuthority] = useState("");
  const [status, setStatus] = useState("planned");
  const [plannedSubmitDate, setPlannedSubmitDate] = useState("");
  const [submitDate, setSubmitDate] = useState("");
  const [plannedDate, setPlannedDate] = useState("");
  const [actualDate, setActualDate] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);
  const attachmentRef = useRef(null);

  useEffect(() => {
    if (isEdit) {
      getPermit(permitId).then((data) => {
        setName(data.name || "");
        setPermitType(data.permit_type || "");
        setAuthority(data.authority || "");
        setStatus(data.status || "planned");
        setPlannedSubmitDate(data.planned_submit_date || "");
        setSubmitDate(data.submit_date || "");
        setPlannedDate(data.planned_date || "");
        setActualDate(data.actual_date || "");
        setDescription(data.description || "");
        setFiles((data.files || []).map((f) => ({ ...f, _id: uuid() })));
      });
    }
  }, [isEdit, permitId]);

  const handleSave = async () => {
    const payload = {
      project: id,
      name,
      permit_type: permitType,
      authority,
      status,
      planned_submit_date: plannedSubmitDate || null,
      submit_date: submitDate || null,
      planned_date: plannedDate || null,
      actual_date: actualDate || null,
      description,
    };

    try {
      let res;
      if (isEdit) {
        res = await updatePermit(permitId, payload);
        alert("인허가 정보가 수정되었습니다.");
      } else {
        res = await createPermit(payload);
        alert("인허가가 등록되었습니다.");
      }

      // ✅ 문서 DNS 추출
      const createdDns = res?.data?.dns || res?.dns || res?.data?.doc_dns;

      // ✅ 첨부 저장
      if (createdDns && attachmentRef.current?.save) {
        console.log("📎 첨부 저장 시작:", createdDns);
        await attachmentRef.current.save(createdDns);
        console.log("📎 첨부 저장 완료");
      }

      navigate(`/project/${id}/permit/list`);
    } catch (err) {
      console.error(err);
      alert("저장 중 오류가 발생했습니다.");
    }
  };

  const handlePickFiles = (e) => {
    const selected = Array.from(e.target.files || []);
    const newFiles = selected.map((file) => ({
      _id: uuid(),
      file,
      description: file.name,
      size: file.size,
      isNew: true,
    }));
    setFiles((prev) => [...prev, ...newFiles]);
    e.target.value = "";
  };

  return (
    <div className="mobile-page-root mb-16">
      <div className="mobile-page-root-inner space-y-3">
        {/* 헤더 */}
        <div className="bg-white rounded-lg p-3 border min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-bold text-gray-900">
                {isEdit ? "인허가 수정" : "인허가 등록"}
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                {isEdit ? "인허가 정보 수정" : "새로운 인허가 등록"}
              </p>
            </div>
            <button
              type="button"
              onClick={handleSave}
              className={`${isEdit ? "btn-edit" : "btn-create"} flex-shrink-0`}
            >
              {isEdit ? "수정" : "등록"}
            </button>
          </div>
        </div>

        {/* 폼 영역 */}
        <div className="bg-white rounded-lg border p-3 min-w-0 space-y-3">
          {/* 기본 정보 */}
          <div className="bg-gray-50 rounded-lg p-3 space-y-3">
            <div className="text-sm font-medium text-gray-900 mb-2">
              기본 정보
            </div>

            {/* 인허가명 */}
            <div>
              <label className="text-xs text-gray-500 mb-1 block">
                인허가명
              </label>
              <input
                type="text"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="인허가명 입력"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            {/* 종류 & 발급기관 */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">종류</label>
                <input
                  type="text"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="종류"
                  value={permitType}
                  onChange={(e) => setPermitType(e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">
                  발급기관
                </label>
                <input
                  type="text"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="발급기관"
                  value={authority}
                  onChange={(e) => setAuthority(e.target.value)}
                />
              </div>
            </div>

            {/* 상태 */}
            <div>
              <label className="text-xs text-gray-500 mb-1 block">상태</label>
              <select
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="planned">계획</option>
                <option value="in_progress">진행중</option>
                <option value="completed">완료</option>
                <option value="delayed">지연</option>
              </select>
            </div>
          </div>

          {/* 날짜 정보 */}
          <div className="bg-gray-50 rounded-lg p-3 space-y-3">
            <div className="text-sm font-medium text-gray-900 mb-2">
              날짜 정보
            </div>

            {/* 접수일 */}
            <div className="space-y-2">
              <div className="text-xs font-medium text-gray-700 mb-1.5">
                접수일
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <div className="bg-white rounded-lg border border-blue-100 p-2.5 space-y-1">
                  <label className="text-xs font-medium text-blue-700 block">
                    예정 접수일
                  </label>
                  <input
                    type="date"
                    className="w-full border-0 bg-transparent px-0 py-1 text-sm text-gray-900 focus:ring-0 focus:outline-none"
                    value={plannedSubmitDate || ""}
                    onChange={(e) => setPlannedSubmitDate(e.target.value)}
                  />
                </div>
                <div className="bg-white rounded-lg border border-green-100 p-2.5 space-y-1">
                  <label className="text-xs font-medium text-green-700 block">
                    실제 접수일
                  </label>
                  <input
                    type="date"
                    className="w-full border-0 bg-transparent px-0 py-1 text-sm text-gray-900 focus:ring-0 focus:outline-none"
                    value={submitDate || ""}
                    onChange={(e) => setSubmitDate(e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* 완료일 */}
            <div className="space-y-2">
              <div className="text-xs font-medium text-gray-700 mb-1.5">
                완료일
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <div className="bg-white rounded-lg border border-blue-100 p-2.5 space-y-1">
                  <label className="text-xs font-medium text-blue-700 block">
                    예정 완료일
                  </label>
                  <input
                    type="date"
                    className="w-full border-0 bg-transparent px-0 py-1 text-sm text-gray-900 focus:ring-0 focus:outline-none"
                    value={plannedDate || ""}
                    onChange={(e) => setPlannedDate(e.target.value)}
                  />
                </div>
                <div className="bg-white rounded-lg border border-green-100 p-2.5 space-y-1">
                  <label className="text-xs font-medium text-green-700 block">
                    실제 완료일
                  </label>
                  <input
                    type="date"
                    className="w-full border-0 bg-transparent px-0 py-1 text-sm text-gray-900 focus:ring-0 focus:outline-none"
                    value={actualDate || ""}
                    onChange={(e) => setActualDate(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* 비고 */}
          <div className="bg-gray-50 rounded-lg p-3">
            <label className="text-xs text-gray-500 mb-1 block">비고</label>
            <textarea
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              rows={4}
              placeholder="비고 입력..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        </div>

        {/* 첨부파일 */}
        <div className="bg-white rounded-lg border p-3 min-w-0 space-y-3">
          <div className="text-sm font-medium text-gray-900">첨부파일</div>

          {/* MobileAttachmentBox 버튼 영역 */}
          <div className="w-full min-w-0">
            <MobileAttachmentBox ref={attachmentRef} />
          </div>

          {/* 기존 파일 목록 */}
          {files.length > 0 && (
            <div className="space-y-2">
              <div className="text-xs text-gray-500 font-medium">
                등록된 파일
              </div>
              {files.map((f) => (
                <div
                  key={f._id}
                  className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 min-w-0"
                >
                  <span className="text-sm truncate text-gray-900 flex-1 min-w-0 pr-2">
                    {f.description}
                  </span>
                  <button
                    onClick={() =>
                      setFiles((prev) => prev.filter((x) => x._id !== f._id))
                    }
                    className="btn-delete-sm flex-shrink-0"
                  >
                    삭제
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
